import type * as React from "react";

import { Box } from "../../components/ui/box";
import { Icon, type IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { cn } from "../../lib/utils";

import {
  useDataTableDensity,
  useDataTableHeaderDrag,
  useDataTableIsFirstHeader,
} from "./data-table-context";
import type {
  DataTableColumnHeaderProps,
  DataTableColumnMeta,
} from "./data-table-types";
import { DENSITY_CONFIG } from "./data-table-utils";

/* Pressed and keyboard-focused header. Written out whole because the modifier
   is part of the class and Tailwind only emits what it can read verbatim. */
const HEADER_PRESSED_CLASS =
  "active:bg-wash-active focus-visible:bg-wash-selected";

// Hover reads at the header CELL (`group/hcell`, owned by data-table), not
// at the sort control: the resize seam sits inside the cell but outside the
// control, so hovering the seam still lights its column. `data-dtresizing`
// is the cell's flag for an active seam drag, when pointer capture keeps
// :hover off the cell — the column being resized holds the hovered look.
const HEADER_HOVERED_WASH =
  "group-hover/hcell:bg-wash-hover group-data-[dtresizing]/hcell:bg-wash-hover";
const HEADER_HOVERED_REVEAL =
  "group-hover/hcell:opacity-100 group-data-[dtresizing]/hcell:opacity-100";
const HEADER_HOVERED_SORT_ICON =
  "group-hover/hcell:block group-data-[dtresizing]/hcell:block";

const HEADER_HOVER_GRADIENT_STYLE: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(to right, transparent 0%, var(--column-header-wash) 100%)",
};

function renderTitle(title: React.ReactNode) {
  // size={null} — the density header text class on TableHead inherits down.
  // Headers are drag/sort surfaces; the header row's select-none inherits too.
  return typeof title === "string" || typeof title === "number" ? (
    <Text size={null} weight="medium" truncate>
      {title}
    </Text>
  ) : (
    title
  );
}

/** Header-cell hover tooltip (`meta.headerTooltip`): title over a dimmed
 *  subtitle — the kanban card field token's read-out grammar. Touch never
 *  opens hover tooltips, so no gate. */
function HeaderTooltip({
  tooltip,
  children,
}: {
  tooltip: DataTableColumnMeta["headerTooltip"];
  children: React.ReactNode;
}) {
  if (!tooltip) return <>{children}</>;
  return (
    <Tooltip>
      {/* The wrapped node fills the header cell — the trigger must pass that
          sizing through (fill + flex) or the hover wash shrinks to the label. */}
      <TooltipTrigger
        render={
          <Box
            fill="height"
            minWidth={0}
            flex={1}
            direction="row"
            align="center"
            className="w-full"
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipPopup side="top">
        <Box gap="xs">
          <Text size="xs">{tooltip.title}</Text>
          {tooltip.subtitle ? (
            // Tooltip surface is fixed dark in both themes — dim via opacity,
            // not a theme color that flips in light mode.
            <Box opacity={70}>
              <Text size="xs">{tooltip.subtitle}</Text>
            </Box>
          ) : null}
        </Box>
      </TooltipPopup>
    </Tooltip>
  );
}

/**
 * Purely decorative: the gradient wash that replaces the flat hover fill on
 * columns that opt in. It carries no information and takes no input
 * (`pointerEvents="none"`), so it stays out of the accessibility tree — the
 * header control itself is what a user, a screen reader and a test address.
 * It publishes `data-slot` because it is the only trace of the opt-in.
 */
function HoverGradientOverlay() {
  return (
    <Box hoverReveal="colhead" pointerEvents="none" fill="absolute" zIndex={0}
      aria-hidden="true"
      data-slot="column-header-hover"
      className={cn(
        "[--column-header-wash:var(--wash-hover)] group-active/colhead:[--column-header-wash:var(--wash-active)] group-active/colhead:opacity-100",
        HEADER_HOVERED_REVEAL,
      )}
      style={HEADER_HOVER_GRADIENT_STYLE}
    />
  );
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
}: DataTableColumnHeaderProps<TData, TValue>): React.ReactElement {
  const canSort = column.getCanSort();
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const density = useDataTableDensity();
  const headerDrag = useDataTableHeaderDrag();
  const canReorder = headerDrag?.reorderableIds.has(column.id) ?? false;
  const densityConfig = DENSITY_CONFIG[density];
  const contentAlignmentClass = meta?.isNumeric
    ? "justify-end"
    : "justify-start";
  const isFirstHeader = useDataTableIsFirstHeader();
  // The first content column defaults to the gradient — it is typically the
  // wide fill column, where the flat wash floods the cell. `headerHoverStyle`
  // stays the explicit override in both directions.
  const usesGradientHover =
    (meta?.headerHoverStyle ?? (isFirstHeader ? "gradient" : "wash")) ===
    "gradient";
  const contentTestId = `data-table-column-header-content-${column.id}`;

  if (!canSort) {
    return (
      <HeaderTooltip tooltip={meta?.headerTooltip}>
      <Box group="colhead" fill="height" minWidth={0} flex={1} overflow="hidden"
        direction="row"
        align="center"
        className={cn(
          "w-full",
          "transition-[background-color]",
          contentAlignmentClass,
          densityConfig.headerPadding,
          canReorder && "cursor-grab",
          !usesGradientHover && HEADER_HOVERED_WASH,
        )}
      >
        {usesGradientHover ? <HoverGradientOverlay /> : null}
        <Box position="relative" zIndex={10} minWidth={0} flex={1}
          direction="row"
          align="center"
          data-testid={contentTestId}
          className={cn(contentAlignmentClass)}
        >
          {renderTitle(title)}
        </Box>
      </Box>
      </HeaderTooltip>
    );
  }

  const sorted = column.getIsSorted();
  const sortIcon: IconName =
    sorted === "asc"
      ? "arrow-up"
      : sorted === "desc"
        ? "arrow-down"
        : "arrow-up-wide-narrow";

  return (
    <HeaderTooltip tooltip={meta?.headerTooltip}>
    <Box fill="height" minWidth={0} flex={1} alignSelf="stretch" overflow="hidden"
      as="button"
      direction="row"
      align="center"
      onClick={() => {
        // A column-reorder drag ends with a pointer-up on this button;
        // swallow the resulting click so dropping a column never sorts it.
        if (headerDrag?.didDragRef.current) return;
        const current = column.getIsSorted();
        if (!current) {
          column.toggleSorting(false);
        } else if (current === "asc") {
          column.toggleSorting(true);
        } else {
          column.clearSorting();
        }
      }}
      className={cn(
        "w-full",
        contentAlignmentClass,
        densityConfig.headerPadding,
        cn(
          "group/colhead outline-none transition-[background-color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/50",
          canReorder ? "cursor-grab" : "cursor-pointer",
          usesGradientHover
            ? "active:bg-transparent focus-visible:bg-transparent"
            : cn(HEADER_HOVERED_WASH, HEADER_PRESSED_CLASS),
        ),
      )}
    >
      {usesGradientHover ? <HoverGradientOverlay /> : null}
      <Box position="relative" zIndex={10} minWidth={0} flex={1}
        direction="row"
        align="center"
        data-testid={contentTestId}
        className={cn(contentAlignmentClass)}
      >
        {renderTitle(title)}
        {/* Unsorted, unhovered: the icon leaves the layout entirely (not just
            transparent) so the label keeps the full cell width — a reserved
            invisible icon truncates narrow columns for chrome nobody sees. */}
        <Icon
          name={sortIcon}
          size="md"
          className={cn(
            "ml-1 shrink-0",
            sorted
              ? "text-foreground"
              : cn(
                  "hidden text-muted-foreground/40 group-focus-visible/colhead:block",
                  HEADER_HOVERED_SORT_ICON,
                ),
          )}
        />
      </Box>
    </Box>
    </HeaderTooltip>
  );
}
